import React, { Component, useState, useEffect, lazy, Suspense } from "react";
import { connect } from "react-redux";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import { getProducts, fetchLikes } from "./store/actions/productActions";
import { SITE_URL, LOGO_SM_URL } from "./config/domain";

import Store from "./components/Store";
import Cart from "./components/Cart";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Login from "./components/Login";
import PrivateRoute from "./components/PrivateRoute";
import HolidayEffects from "./components/HolidayEffects";

// ── iOS / low-memory detection ────────────────────────────────
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
// iOS Safari crashes with GPU-heavy holiday effects (will-change + emoji + RAF)
// Disable on ALL iOS devices to prevent "A problem repeatedly occurred"
const isLowMemory = isIOS || !window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

/* ─────────────────────────────
   Lazy pages
───────────────────────────── */
// ── Page loading fallback ─────────────────────────────────────
const PageLoader = () => (
  <div style={{
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    minHeight: 'calc(var(--dvh, 1vh) * 60)', gap: 20,
    background: 'var(--bv-bg, #fff6e8)',
  }}>
    <div style={{ position: 'relative', width: 56, height: 56 }}>
      {/* Spinning ring */}
      <svg viewBox="0 0 56 56" width="56" height="56" style={{ position: 'absolute', top: 0, left: 0, animation: 'pl-spin 1.1s linear infinite' }}>
        <circle cx="28" cy="28" r="23" fill="none" stroke="var(--bv-border,#e8d5b0)" strokeWidth="3.5"/>
        <circle cx="28" cy="28" r="23" fill="none"
          stroke="url(#pl-grad)" strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray="40 105"
          strokeDashoffset="0"
        />
        <defs>
          <linearGradient id="pl-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="var(--bv-primary,#e8390a)"/>
            <stop offset="100%" stopColor="var(--bv-accent,#f5c200)"/>
          </linearGradient>
        </defs>
      </svg>
      {/* Logo in center */}
      <img
        src={LOGO_SM_URL}
        alt=""
        style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 28, height: 28, objectFit: 'contain' }}
        onError={e => e.target.style.display = 'none'}
      />
    </div>
    {/* Animated dots */}
    <div style={{ display: 'flex', gap: 6 }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          display: 'block', width: 7, height: 7, borderRadius: '50%',
          background: 'var(--bv-primary,#e8390a)',
          animation: `pl-dot 1.2s ease-in-out infinite`,
          animationDelay: `${i * 0.2}s`,
          opacity: 0.8,
        }}/>
      ))}
    </div>
    <style>{`
      @keyframes pl-spin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
      @keyframes pl-dot {
        0%,80%,100% { transform: scale(0.5); opacity: 0.25; }
        40%          { transform: scale(1.2); opacity: 1; }
      }
    `}</style>
  </div>
);

const AdminPanel = lazy(() => import("./components/AdminPanel"));
const POSPage    = lazy(() => import("./components/POSPage"));
const Profile = lazy(() => import("./components/Profile"));
const AboutPage = lazy(() => import("./components/AboutPage"));
const ContactPage = lazy(() => import("./components/ContactPage"));
const DeliveryPage = lazy(() => import("./components/DeliveryPage"));
const HowItWorksPage = lazy(() => import("./components/HowItWorksPage"));
const LandingTorontoProduce   = lazy(() => import("./components/LandingTorontoProduce"));
const LandingOntarioWholesale = lazy(() => import("./components/LandingOntarioWholesale"));
const LandingFreshVegetables  = lazy(() => import("./components/LandingFreshVegetables"));
const LandingWholesaleProduce  = lazy(() => import("./components/LandingWholesaleProduce"));
const LandingWholesaleFruit    = lazy(() => import("./components/LandingWholesaleFruit"));
const LandingProduceDelivery   = lazy(() => import("./components/LandingProduceDelivery"));
const LandingProduceRestaurants= lazy(() => import("./components/LandingProduceRestaurants"));
const NotFoundPage             = lazy(() => import("./components/NotFoundPage"));

/* ─────────────────────────────
   SAFE STORAGE (iOS SAFE)
───────────────────────────── */
function safeGet(key) {
  try {
    if (typeof window === "undefined") return null;
    if (!window.localStorage) return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/* ─────────────────────────────
   AUTO OPEN SAFE (NO LOOP)
───────────────────────────── */
function shouldAutoOpen() {
  try {
    if (typeof window === "undefined") return false;

    const hasUser = !!safeGet("userEmail");
    const flag = safeGet("bv_auto_open_admin");
    const isAdmin = safeGet("bv_is_admin") === "1";
    const path = window.location.pathname;

    // Не редіректимо якщо вже на /fanbox або /login
    if (path.startsWith("/fanbox") || path.startsWith("/login")) return false;

    // Редіректимо адміна з будь-якої сторінки (не тільки з /)
    if (!hasUser || !isAdmin) return false;

    return flag === "TRUE";
  } catch {
    return false;
  }
}

/* ─────────────────────────────
   Scroll button
───────────────────────────── */
const ScrollToTopBtn = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 320);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      className="scroll-top-btn"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Scroll to top"
    >
      ↑
    </button>
  );
};

/* ─────────────────────────────
   AppShell — hides store Header/Footer on admin route
───────────────────────────── */
function AppShell({ children }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/fanbox");
  return (
    <div className="app-wrapper">
      {!isLowMemory && !isAdmin && <HolidayEffects />}
      {!isAdmin && <Header />}
      <main id="main-content">
        {children}
      </main>
      {!isAdmin && <Footer />}
      <ScrollToTopBtn />
    </div>
  );
}

/* ─────────────────────────────
   App
───────────────────────────── */
class App extends Component {
  _redirectDone = false;

  componentDidMount() {
    // iOS SAFE redirect (delayed) — перевіряємо is_admin через сервер
    setTimeout(async () => {
      try {
        if (!this._redirectDone && shouldAutoOpen()) {
          // Верифікуємо статус адміна через сервер перед редіректом
          const email = safeGet("userEmail");
          try {
            const res = await fetch(`${SITE_URL}/api/otp`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "check_admin", email }),
            });
            const d = await res.json();
            if (!d.is_admin) {
              // Не адмін більше — очищаємо флаг
              try { localStorage.setItem("bv_is_admin", "0"); } catch {}
              return;
            }
          } catch {
            // Якщо сервер недоступний — довіряємо localStorage
          }
          this._redirectDone = true;
          window.location.replace("/fanbox");
        }
      } catch {}
    }, 80);

    this.props.getProducts();

    if (safeGet("userEmail")) {
      this.props.fetchLikes();
    }

    this._onStorage = (e) => {
      if (e.key === "bv_products_cache" && e.newValue === null) {
        this.props.getProducts();
      }
      if (e.key === "bv_reload_flag") {
        this.props.getProducts();
      }
    };

    this._onVisible = () => {
      if (document.visibilityState === "visible") {
        this.props.getProducts();
      }
    };

    window.addEventListener("storage", this._onStorage);
    document.addEventListener("visibilitychange", this._onVisible);
  }

  componentWillUnmount() {
    window.removeEventListener("storage", this._onStorage);
    document.removeEventListener("visibilitychange", this._onVisible);
  }

  render() {
    // ── Admin access config ──────────────────────────────────────────
    // true  → /fanbox і /pos доступні на ВСІХ доменах
    // false → тільки на frontend-хості з config/domain.js (FRONTEND_HOST)
    const ADMIN_ON_MAIN_WEB = true;

    const isVercel =
      typeof window !== "undefined" &&
      window.location.hostname.includes("vercel.app");

    const showAdmin = ADMIN_ON_MAIN_WEB || isVercel;

    return (
      <BrowserRouter>
        <AppShell>
          <Routes>

              {/* ADMIN — керується конфігом ADMIN_ON_MAIN_WEB */}
              <Route path="/fanbox" element={
                showAdmin
                  ? <Suspense fallback={<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"calc(var(--dvh,1vh)*100)",background:"var(--bv-bg,#fff6e8)"}}><img src={LOGO_SM_URL} alt="" style={{width:56,height:56,opacity:0.7,animation:"adm-pulse 1.2s ease-in-out infinite"}} onError={e=>e.target.style.display="none"}/></div>}><AdminPanel /></Suspense>
                  : <Store />
              } />

              <Route path="/pos" element={
                showAdmin
                  ? <Suspense fallback={<PageLoader />}><POSPage /></Suspense>
                  : <Store />
              } />

              {/* MAIN */}
              <Route index element={<Store />} />
              <Route path="store" element={<Store />} />
              <Route path="cart" element={<Cart />} />

              <Route path="about" element={<Suspense fallback={<PageLoader />}><AboutPage /></Suspense>} />

              <Route path="contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />

              <Route path="delivery" element={<Suspense fallback={<PageLoader />}><DeliveryPage /></Suspense>} />

              <Route path="how-it-works" element={<Suspense fallback={<PageLoader />}><HowItWorksPage /></Suspense>} />

              {/* PROFILE */}
              <Route path="profile" element={<PrivateRoute><Suspense fallback={<PageLoader />}><Profile /></Suspense></PrivateRoute>} />

              <Route path="login" element={<Login />} />

              {/* SEO Landing Pages */}
              <Route path="toronto-produce"         element={<Suspense fallback={<PageLoader />}><LandingTorontoProduce /></Suspense>} />
              <Route path="ontario-wholesale"       element={<Suspense fallback={<PageLoader />}><LandingOntarioWholesale /></Suspense>} />
              <Route path="fresh-vegetables-toronto" element={<Suspense fallback={<PageLoader />}><LandingFreshVegetables /></Suspense>} />
              <Route path="wholesale-produce-toronto"    element={<Suspense fallback={<PageLoader />}><LandingWholesaleProduce /></Suspense>} />
              <Route path="wholesale-fruit-toronto"      element={<Suspense fallback={<PageLoader />}><LandingWholesaleFruit /></Suspense>} />
              <Route path="produce-delivery-toronto"     element={<Suspense fallback={<PageLoader />}><LandingProduceDelivery /></Suspense>} />
              <Route path="produce-for-restaurants-toronto" element={<Suspense fallback={<PageLoader />}><LandingProduceRestaurants /></Suspense>} />

              <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />

            </Routes>
        </AppShell>
      </BrowserRouter>
    );
  }
}

const mapStateToProps = (state) => ({
  products: state.products,
});

const mapDispatchToProps = (dispatch) => ({
  getProducts: () => dispatch(getProducts()),
  fetchLikes: () => dispatch(fetchLikes()),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
